interface IPaymentCheckoutOrderFeeGroup {
  orderIds: string[];
  serviceCategory: string;
}

/** Query flag set only by unpaid invoice table Pay Now; enables per-order serviceCategory fee grouping. */
const UNPAID_INVOICE_CHECKOUT_QUERY_PARAM = 'unpaidInvoiceCheckout';
const UNPAID_INVOICE_CHECKOUT_QUERY_VALUE = 'true';
/** Comma-joined numbers being paid (invoice number, else order number); checkout sends them to bank transfer reserve. */
const PAYMENT_ITEMS_QUERY_PARAM = 'paymentItems';

/**
 * Builds order-fee API groups from checkout path orderIds and query `serviceCategory`.
 * Supports comma-aligned lists (invoice mix) and single category (order table / legacy URLs).
 * @param {string[]} orderIds - Checkout path order ids.
 * @param {string} serviceCategoryQuery - Raw `serviceCategory` query (comma-separated or single).
 * @returns {IPaymentCheckoutOrderFeeGroup[]} Groups for GET order-fee calls.
 */
function buildCheckoutOrderFeeGroups(orderIds: string[], serviceCategoryQuery: string): IPaymentCheckoutOrderFeeGroup[] {
  if (!orderIds.length) {
    return [];
  }

  const trimmedCategories: string[] = serviceCategoryQuery
    .split(',')
    .map((value: string): string => value.trim())
    .filter((value: string): boolean => value.length > 0);
  const fallbackCategory: string = trimmedCategories[0] ?? '';

  if (trimmedCategories.length <= 1) {
    return [
      {
        orderIds: [...orderIds],
        serviceCategory: fallbackCategory,
      },
    ];
  }

  if (trimmedCategories.length !== orderIds.length) {
    return [
      {
        orderIds: [...orderIds],
        serviceCategory: fallbackCategory,
      },
    ];
  }

  const orderIdsByCategory = new Map<string, string[]>();

  orderIds.forEach((orderId: string, index: number): void => {
    const serviceCategory: string = trimmedCategories[index] ?? fallbackCategory;
    const existing: string[] = orderIdsByCategory.get(serviceCategory) ?? [];

    existing.push(orderId);
    orderIdsByCategory.set(serviceCategory, existing);
  });

  return Array.from(orderIdsByCategory.entries()).map(
    ([serviceCategory, ids]: [string, string[]]): IPaymentCheckoutOrderFeeGroup => ({
      serviceCategory,
      orderIds: ids,
    })
  );
}

export { buildCheckoutOrderFeeGroups, PAYMENT_ITEMS_QUERY_PARAM, UNPAID_INVOICE_CHECKOUT_QUERY_PARAM, UNPAID_INVOICE_CHECKOUT_QUERY_VALUE };

export type { IPaymentCheckoutOrderFeeGroup };
